import React from 'react';
import { Button, makeStyles, shorthands } from '@fluentui/react-components';
import { ArrowLeft24Regular, Delete16Regular, Edit16Regular } from '@fluentui/react-icons';
import { customColors, customFonts } from '../../../../../styles';
import type { UsePersonaVm } from '../../../hooks/usePersona';

const useStyles = makeStyles({
    backButtonContainer: {
        ...shorthands.margin('0px', '0px', '8px', '0px'),
    },
    backButton: {
        height: '28px',
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        fontWeight: '700',
        textDecoration: 'underline',
        color: customColors.protivitiDarkBlue,
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('0px', '10px'),
        backgroundColor: customColors.white,
        '&:hover': {
            color: customColors.summarizeButtonDarker,
        },
    },
    personaLibraryEntriesScrollArea: {
        maxHeight: '260px',
        overflowY: 'auto',
        ...shorthands.padding('0px', '2px', '0px', '0px'),
    },
    personaLibraryList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    personaLibraryItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        ...shorthands.border('1px', 'solid', '#D9D9D9'),
        ...shorthands.borderRadius('6px'),
        ...shorthands.padding('10px'),
        backgroundColor: customColors.white,
    },
    personaLibraryItemHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
    },
    personaLibraryItemTitle: {
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        fontWeight: '700',
        color: customColors.protivitiDarkBlue,
    },
    personaLibraryItemPreview: {
        fontFamily: customFonts.Lato,
        fontSize: '14px',
        color: customColors.summarizeButtonDarker,
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
    },
    personaLibraryItemActions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
    },
    personaLibraryActionButton: {
        fontFamily: customFonts.Lato,
        fontSize: '12px',
        fontWeight: '600',
        minWidth: '90px',
    },
    personaLibraryActionIcons: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginLeft: '8px',
        flexShrink: 0,
    },
    personaLibraryIconButton: {
        minWidth: '24px',
        width: '24px',
        height: '24px',
        color: customColors.protivitiDarkBlue,
    },
    emptyText: {
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        color: customColors.summarizeButtonDarker,
    },
});

interface PersonaLibraryViewProps {
    vm: UsePersonaVm;
}

const PersonaLibraryView: React.FC<PersonaLibraryViewProps> = ({ vm }) => {
    const styles = useStyles();

    return (
        <>
            <div className={styles.backButtonContainer}>
                <Button
                    appearance="transparent"
                    className={styles.backButton}
                    icon={<ArrowLeft24Regular />}
                    onClick={vm.closePersonaLibrary}
                >
                    Back
                </Button>
            </div>
            <div className={styles.personaLibraryEntriesScrollArea}>
                <div className={styles.personaLibraryList}>
                    {vm.savedPersonas.length === 0 ? (
                        <div className={styles.emptyText}>No saved personas yet.</div>
                    ) : (
                        vm.savedPersonas.map((persona) => (
                            <div key={persona.id} className={styles.personaLibraryItem}>
                                <div className={styles.personaLibraryItemHeader}>
                                    <span className={styles.personaLibraryItemTitle}>{persona.title}</span>
                                </div>
                                <div className={styles.personaLibraryItemPreview}>{persona.content}</div>
                                <div className={styles.personaLibraryItemActions}>
                                    <Button
                                        className={styles.personaLibraryActionButton}
                                        appearance="secondary"
                                        onClick={() => vm.selectLibraryPersona(persona)}
                                    >
                                        Use
                                    </Button>
                                    <div className={styles.personaLibraryActionIcons}>
                                        <Button
                                            appearance="subtle"
                                            icon={<Edit16Regular />}
                                            className={styles.personaLibraryIconButton}
                                            aria-label="Edit saved persona"
                                            onClick={() => vm.editLibraryPersona(persona)}
                                        />
                                        <Button
                                            appearance="subtle"
                                            icon={<Delete16Regular />}
                                            className={styles.personaLibraryIconButton}
                                            aria-label="Delete saved persona"
                                            onClick={() => vm.openDeletePersonaDialog(persona)}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </>
    );
};

export default PersonaLibraryView;
